import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

// ── Palette (matches PropertyPincoder app) ──
const OUTER_BG = 'rgb(239, 231, 221)';
const CARD_BG = '#FFFDF8';
const BORDER = '#EFE6D6';
const TITLE_DARK = '#2B2118';
const SUBTLE = '#7A6A5A';
const ACCENT = '#8A6A3D';
const ACCENT_DARK = '#6B3620';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Section renderer (keeps JSX DRY) ──
  const Section = ({ number, title, children }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: '#F4E7CE',
            color: ACCENT_DARK,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: '0.72rem',
            flexShrink: 0,
            border: `1px solid #E8D4A8`,
          }}
        >
          {number}
        </Box>
        <Typography
          sx={{
            fontWeight: 800,
            color: TITLE_DARK,
            fontSize: '0.95rem',
            letterSpacing: 0.1,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ pl: { xs: 0, sm: 4.5 } }}>{children}</Box>
    </Box>
  );

  // ── Bullet row (for lists inside sections) ──
  const Bullet = ({ primary, secondary }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        py: 0.5,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: ACCENT,
          mt: '7px',
          flexShrink: 0,
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ fontSize: '0.82rem', color: TITLE_DARK, fontWeight: 600, lineHeight: 1.4 }}
        >
          {primary}
        </Typography>
        {secondary && (
          <Typography
            sx={{ fontSize: '0.75rem', color: SUBTLE, lineHeight: 1.4, mt: 0.2 }}
          >
            {secondary}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: OUTER_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 2, sm: 4 },
        px: { xs: 1.5, sm: 2 },
      }}
    >
      <Container maxWidth="md" disableGutters>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: isMobile ? '92vh' : '85vh',
            borderRadius: '20px',
            overflow: 'hidden',
            background: CARD_BG,
            border: `1px solid ${BORDER}`,
            boxShadow: '0 10px 30px rgba(0,0,0,0.10)',
          }}
        >
          {/* ===== HEADER ===== */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              position: 'sticky',
              top: 0,
              backgroundColor: CARD_BG,
              zIndex: 10,
              borderBottom: `1px solid ${BORDER}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#F4E7CE',
                color: ACCENT_DARK,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '1px solid #E8D4A8',
              }}
            >
              <ShieldOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', sm: '1.1rem' },
                  color: TITLE_DARK,
                  lineHeight: 1.2,
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  color: SUBTLE,
                  fontWeight: 600,
                  mt: 0.2,
                }}
              >
                PropertyPincoder · Effective {new Date().toLocaleDateString()}
              </Typography>
            </Box>

            <Chip
              label="v1.0"
              size="small"
              sx={{
                bgcolor: '#F4E7CE',
                color: ACCENT_DARK,
                fontWeight: 700,
                fontSize: '0.68rem',
                height: 22,
                border: '1px solid #E8D4A8',
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            />
          </Box>

          {/* ===== SCROLLABLE CONTENT ===== */}
          <Box
            sx={{
              overflowY: 'auto',
              px: { xs: 2, sm: 3 },
              py: 2.5,
              flex: 1,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#D8C7AA',
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            }}
          >
            {/* Intro paragraph (not numbered) */}
            <Typography
              sx={{
                fontSize: '0.82rem',
                color: SUBTLE,
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              This Privacy Policy explains how <strong>PropertyPincoder</strong>, operated by
              JK Constructions And Interiors, collects, uses, and protects your personal
              information when you access our website (
              <Box component="span" sx={{ color: ACCENT_DARK, fontWeight: 700 }}>
                https://propertypincoder.com
              </Box>
              ) or mobile application.
            </Typography>

            {/* 1. Introduction */}
            <Section number="1" title="Introduction">
              <Typography sx={{ fontSize: '0.82rem', color: SUBTLE, lineHeight: 1.6 }}>
                This Privacy Policy explains how <strong>PropertyPincoder</strong>, operated by
                JK Constructions And Interiors, collects, uses, and protects your personal
                information when you access our website (
                <Box component="span" sx={{ color: ACCENT_DARK, fontWeight: 700 }}>
                  https://propertypincoder.com
                </Box>
                ) or mobile application.
              </Typography>
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 2. Information We Collect */}
            <Section number="2" title="Information We Collect">
              <Bullet
                primary="Personal Information"
                secondary="Name, email address, phone number"
              />
              <Bullet
                primary="Location Information"
                secondary="City, area, and property location"
              />
              <Bullet
                primary="Property Details"
                secondary="Buy, rent, lease listings, images, pricing, and descriptions"
              />
              <Bullet
                primary="Account Information"
                secondary="Login credentials and profile details for agents, builders, and owners"
              />
              <Bullet
                primary="Technical Information"
                secondary="Device information, IP address, and usage logs"
              />
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 3. How We Use Your Information */}
            <Section number="3" title="How We Use Your Information">
              <Bullet primary="Provide buy, rent, and lease services" />
              <Bullet primary="Enable agents, builders, and owners to manage listings" />
              <Bullet primary="Facilitate communication between buyers, renters, and sellers" />
              <Bullet primary="Improve platform performance and user experience" />
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 4. Data Sharing */}
            <Section number="4" title="Data Sharing">
              <Typography sx={{ fontSize: '0.82rem', color: SUBTLE, lineHeight: 1.6 }}>
                We may share your data with agents, builders, owners, or trusted service
                providers strictly for service delivery. We do not sell your personal data to
                third parties.
              </Typography>
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 5. Third-Party Links */}
            <Section number="5" title="Third-Party Links">
              <Typography sx={{ fontSize: '0.82rem', color: SUBTLE, lineHeight: 1.6 }}>
                PropertyPincoder may contain links to third-party websites. We are not
                responsible for their privacy practices. Please review their policies
                separately.
              </Typography>
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 6. Data Security */}
            <Section number="6" title="Data Security">
              <Typography sx={{ fontSize: '0.82rem', color: SUBTLE, lineHeight: 1.6 }}>
                We use industry-standard security measures, including secure servers and
                encryption in transit, to protect your data.
              </Typography>
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 7. Your Rights */}
            <Section number="7" title="Your Rights">
              <Bullet primary="Access or update your personal information" />
              <Bullet primary="Request deletion of your account and data" />
              <Bullet primary="Withdraw consent for marketing communications" />
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 8. Data Retention */}
            <Section number="8" title="Data Retention">
              <Typography sx={{ fontSize: '0.82rem', color: SUBTLE, lineHeight: 1.6 }}>
                We retain your information only as long as necessary to provide services or
                comply with legal obligations.
              </Typography>
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 9. Children's Privacy */}
            <Section number="9" title="Children's Privacy">
              <Typography sx={{ fontSize: '0.82rem', color: SUBTLE, lineHeight: 1.6 }}>
                PropertyPincoder services are not intended for individuals under 18 years of
                age.
              </Typography>
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 10. Changes to This Policy */}
            <Section number="10" title="Changes to This Policy">
              <Typography sx={{ fontSize: '0.82rem', color: SUBTLE, lineHeight: 1.6 }}>
                We may update this Privacy Policy from time to time. Changes will be effective
                upon posting on{' '}
                <Box component="span" sx={{ color: ACCENT_DARK, fontWeight: 700 }}>
                  https://propertypincoder.com
                </Box>
                .
              </Typography>
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 11. Contact Us */}
            <Section number="11" title="Contact Us">
              <Typography sx={{ fontSize: '0.82rem', color: SUBTLE, lineHeight: 1.6 }}>
                For questions, data access, or deletion requests, contact us at{' '}
                <Box component="span" sx={{ color: ACCENT_DARK, fontWeight: 700 }}>
                  propertypincoderhelp@gmail.com
                </Box>
                .
              </Typography>
            </Section>

            <Divider sx={{ my: 2, borderColor: BORDER }} />

            {/* 12. Governing Law */}
            <Section number="12" title="Governing Law">
              <Typography sx={{ fontSize: '0.82rem', color: SUBTLE, lineHeight: 1.6 }}>
                This Privacy Policy is governed by the laws of India.
              </Typography>
            </Section>

            <Typography
              sx={{
                fontStyle: 'italic',
                fontSize: '0.82rem',
                color: SUBTLE,
                mt: 3,
                mb: 1,
                lineHeight: 1.6,
              }}
            >
              By using our Services, you acknowledge that you have read, understood, and agree
              to be bound by this Privacy Policy.
            </Typography>
          </Box>

          {/* ===== FOOTER ===== */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderTop: `1px solid ${BORDER}`,
              backgroundColor: CARD_BG,
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              gap: 1.2,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/')}
              sx={{
                borderColor: '#D8C7AA',
                color: ACCENT_DARK,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.1,
                '&:hover': {
                  borderColor: ACCENT,
                  backgroundColor: '#FBF4E8',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/')}
              sx={{
                backgroundColor: ACCENT_DARK,
                color: '#FFFFFF',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.1,
                boxShadow: '0 4px 12px rgba(107,54,32,0.25)',
                '&:hover': {
                  backgroundColor: '#5A2C1A',
                  boxShadow: '0 6px 16px rgba(107,54,32,0.35)',
                },
              }}
            >
              I have read and accept the Privacy Policy
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;